import type { ReactNode } from "react";
import type React from "react";
import { useCallback, useRef } from "react";

import { ListItemPlaceholderLoader, TitlePlaceholderLoader } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { hasSegmentsClaim } from "app/pages/segments/config/segmentsConfigHelpers";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";
import { FadeWrapper } from "app/UtilitiesAndConstants/UtilitiesComponents/FadeWrapper";

import type { IDomain, IDomains } from "../../MarketBuilder.types";

import { EmptyDomains } from "./EmptyDomains";
import { SelectedDomainItem } from "./SelectedDomainItem";
import {
    DomainsContainer,
    DomainsCounterContainer,
    SelectedDomainsListContainer,
    ContentContainer,
    HeaderExtraRow,
    Title,
    StyledFadeWrapper,
    LoadersWrapper,
    LoadersListItemWrapper,
    FadeWrapperContainer,
} from "./SelectedDomainsList.styles";
import { SelectedSegmentItem } from "./SelectedSegmentItem";

interface ISelectedDomainsListProps {
    domains: IDomains;
    maxAllowedDomains: number;
    maxAllowedSegments: number;
    onRemoveEntity: (entity: IDomain) => void;
    isLoading: boolean;
    domainValidator: (entity: IDomain) => boolean;
    // Only the V2 manual/agentic view needs the country selector inside this widget (Figma
    // node 75:5300); V1 keeps it in the top bar, so this stays undefined there.
    headerExtra?: ReactNode;
}

export const SelectedDomainsList: React.FC<ISelectedDomainsListProps> = (props) => {
    const { domains, onRemoveEntity, maxAllowedDomains, maxAllowedSegments, isLoading, domainValidator, headerExtra } =
        props;
    const previousDomains = useRef<IDomains>(domains);
    const renderHomepageItems = useCallback(
        (domainsList: IDomain[]) => {
            if (!domainsList.length) {
                return <EmptyDomains />;
            }
            const items = domainsList.map((entity, index) => {
                const { domain, type, id } = entity;
                if (type === EAssetType.Segment) {
                    return (
                        <SelectedSegmentItem
                            key={`${id}_${index}`}
                            entity={entity}
                            onDeleteSegment={() => onRemoveEntity(entity)}
                            isNewSegment={previousDomains?.current?.every((previousDomain) => previousDomain.id !== id)}
                        />
                    );
                } else {
                    return (
                        <SelectedDomainItem
                            key={`${domain}_${index}`}
                            entity={entity}
                            state={domainValidator(entity) ? "default" : "error"}
                            onDeleteDomain={() => onRemoveEntity(entity)}
                            isNewDomain={previousDomains?.current?.every(
                                (previousDomain) => previousDomain.domain !== domain,
                            )}
                        />
                    );
                }
            });
            previousDomains.current = domainsList;
            return items;
        },
        [onRemoveEntity, domainValidator],
    );

    const getEntityCountText = () => (
        <>
            {domains.length} / {maxAllowedDomains} {translate("market.builder.side.entities")}
        </>
    );

    const getSegmentsLength = () => {
        let segmentsLength = 0;
        domains.forEach((domain) => {
            if (domain.type === EAssetType.Segment) {
                segmentsLength++;
            }
        });
        return segmentsLength;
    };

    const getSegmentsCountText = () => {
        if (!hasSegmentsClaim()) {
            return;
        }
        return <>{`| ${getSegmentsLength()} / ${maxAllowedSegments} ${translate("market.builder.side.segments")}`}</>;
    };

    const listItemLoader = () => {
        return (
            <LoadersWrapper>
                <TitlePlaceholderLoader
                    id={`market-builder__your-list-ll`}
                    className="Loader--title"
                />
                <LoadersListItemWrapper>
                    {Array.from({ length: 3 }, (_, i) => (
                        <ListItemPlaceholderLoader
                            key={i}
                            id={`your_list_li${i}`}
                            className="Loader--item"
                        />
                    ))}
                </LoadersListItemWrapper>
            </LoadersWrapper>
        );
    };

    return (
        <SelectedDomainsListContainer>
            {isLoading ? (
                listItemLoader()
            ) : (
                <>
                    <Title>{translate("market.builder.your.list")}</Title>
                    {headerExtra && <HeaderExtraRow>{headerExtra}</HeaderExtraRow>}
                    <DomainsCounterContainer>
                        {getEntityCountText()} {getSegmentsCountText()}
                    </DomainsCounterContainer>
                    <ContentContainer>
                        <DomainsContainer>
                            <FadeWrapperContainer>
                                <FadeWrapper
                                    fadeHeight={"45px"}
                                    ContentContainer={StyledFadeWrapper}
                                >
                                    {renderHomepageItems(domains)}
                                </FadeWrapper>
                            </FadeWrapperContainer>
                        </DomainsContainer>
                    </ContentContainer>
                </>
            )}
        </SelectedDomainsListContainer>
    );
};
